import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { inventarioAPI, handleAPIError } from '../../services/api';

export const fetchInventory = createAsyncThunk(
  'inventory/fetchInventory',
  async (filters = {}, { rejectWithValue }) => {
    try {
      const response = await inventarioAPI.getAll(filters);
      return response.data;
    } catch (error) {
      return rejectWithValue(handleAPIError(error));
    }
  }
);

export const createInventory = createAsyncThunk(
  'inventory/createInventory',
  async (inventoryData, { rejectWithValue }) => {
    try {
      const response = await inventarioAPI.create(inventoryData);
      return response.data;
    } catch (error) {
      return rejectWithValue(handleAPIError(error));
    }
  }
);

export const updateInventory = createAsyncThunk(
  'inventory/updateInventory',
  async ({ id, inventoryData }, { rejectWithValue }) => {
    try {
      const response = await inventarioAPI.update(id, inventoryData);
      return response.data;
    } catch (error) {
      return rejectWithValue(handleAPIError(error));
    }
  }
);

export const deleteInventory = createAsyncThunk(
  'inventory/deleteInventory',
  async (id, { rejectWithValue }) => {
    try {
      await inventarioAPI.delete(id);
      return id;
    } catch (error) {
      return rejectWithValue(handleAPIError(error));
    }
  }
);

export const addInventoryEvent = createAsyncThunk(
  'inventory/addInventoryEvent',
  async (eventData, { rejectWithValue }) => {
    try {
      const response = await inventarioAPI.addEvent(eventData);
      return response.data;
    } catch (error) {
      return rejectWithValue(handleAPIError(error));
    }
  }
);

export const reduceInventoryEvent = createAsyncThunk(
  'inventory/reduceInventoryEvent',
  async (eventData, { rejectWithValue }) => {
    try {
      const response = await inventarioAPI.reduceEvent(eventData);
      return response.data;
    } catch (error) {
      return rejectWithValue(handleAPIError(error));
    }
  }
);

const initialState = {
  items: [],
  loading: false,
  error: null,
  filters: {
    search: '',
    location: 'all',
    boxType: 'all'
  }
};

const inventorySlice = createSlice({
  name: 'inventory',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {
        search: '',
        location: 'all',
        boxType: 'all'
      };
    },
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchInventory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchInventory.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(fetchInventory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createInventory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createInventory.fulfilled, (state, action) => {
        state.loading = false;
        state.items = [action.payload, ...state.items];
      })
      .addCase(createInventory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateInventory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateInventory.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.items.findIndex(item => item.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
      })
      .addCase(updateInventory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteInventory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteInventory.fulfilled, (state, action) => {
        state.loading = false;
        state.items = state.items.filter(item => item.id !== action.payload);
      })
      .addCase(deleteInventory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(addInventoryEvent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addInventoryEvent.fulfilled, (state) => {
        state.loading = false;
        // Inventory will be refreshed by the component
      })
      .addCase(addInventoryEvent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(reduceInventoryEvent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(reduceInventoryEvent.fulfilled, (state) => {
        state.loading = false;
        // Inventory will be refreshed by the component
      })
      .addCase(reduceInventoryEvent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const {
  setFilters,
  clearFilters,
  clearError
} = inventorySlice.actions;

export const selectInventoryItems = (state) => state.inventory.items;
export const selectInventoryLoading = (state) => state.inventory.loading;
export const selectInventoryError = (state) => state.inventory.error;
export const selectInventoryFilters = (state) => state.inventory.filters;

export const selectFilteredInventory = (state) => {
  const items = selectInventoryItems(state);
  const filters = selectInventoryFilters(state);

  return items
    .filter((item) => {
      const matchesLocation = filters.location === 'all' || item.ubicacion === filters.location;

      const matchesSearch = !filters.search ||
        item.productos?.nombre?.toLowerCase().includes(filters.search.toLowerCase()) ||
        item.productos?.code?.toLowerCase().includes(filters.search.toLowerCase()) ||
        item.observaciones?.toLowerCase().includes(filters.search.toLowerCase());

      const matchesBoxType = filters.boxType === 'all' || item.productos?.tipo_caja?.tipo === filters.boxType;

      return matchesLocation && matchesSearch && matchesBoxType;
    })
    .sort((a, b) => a.id - b.id);
};

export default inventorySlice.reducer;
